import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';

const AssessmentDropdown = ({ userId, onClose }) => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      loadUserAssessments();
    }
  }, [userId]);

  const loadUserAssessments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_assessments')
        .select(`
          id,
          assessment_setup,
          is_completed,
          created_at,
          updated_at
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10); // Show last 10 assessments

      if (error) throw error;
      setAssessments(data || []);
    } catch (error) {
      console.error('Error loading assessments:', error);
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssessmentClick = (assessment) => {
    onClose();
    
    if (assessment.is_completed) {
      // Navigate to AI Plan generation with this assessment
      navigate('/ai-plan-generation', {
        state: {
          assessmentSetup: assessment.assessment_setup,
          assessmentId: assessment.id
        }
      });
    } else {
      // Navigate to questionnaire to continue this assessment
      navigate('/adaptive-questionnaire', {
        state: {
          assessmentSetup: assessment.assessment_setup,
          assessmentId: assessment.id,
          continueExisting: true
        }
      });
    }
  };

  const handleViewAllAssessments = () => {
    onClose();
    navigate('/credits');
  };

  if (loading) {
    return (
      <div className="px-4 py-3">
        <div className="flex items-center space-x-2">
          <Icon name="Loader2" size={16} className="animate-spin" />
          <span className="text-sm text-muted-foreground">Loading assessments...</span>
        </div>
      </div>
    );
  }

  if (assessments.length === 0) {
    return (
      <div className="px-4 py-3">
        <div className="text-center">
          <Icon name="ClipboardList" size={24} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground mb-2">No assessments yet</p>
          <button
            onClick={() => {
              onClose();
              navigate('/career-assessment-setup');
            }}
            className="text-xs text-primary hover:text-primary/80 underline"
          >
            Start your first assessment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-64 overflow-y-auto">
      <div className="px-4 py-2 border-b border-border">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Recent Assessments
        </p>
      </div>
      
      {assessments.map((assessment) => {
        const occupation = assessment.assessment_setup?.occupation || 'Unknown Occupation';
        const seniority = assessment.assessment_setup?.seniority || 'Unknown Level';
        const isCompleted = assessment.is_completed;
        
        return (
          <button
            key={assessment.id}
            onClick={() => handleAssessmentClick(assessment)}
            className="flex items-center space-x-3 px-4 py-3 text-sm text-popover-foreground hover:bg-muted transition-colors duration-200 w-full text-left"
          >
            <div className="flex-shrink-0">
              <Icon 
                name={isCompleted ? "CheckCircle" : "Clock"} 
                size={16} 
                className={isCompleted ? "text-success" : "text-warning"} 
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{occupation}</p>
              <p className="text-xs text-muted-foreground">
                {seniority} • {format(new Date(assessment.created_at), 'MMM dd')}
              </p>
            </div>
            <div className="flex-shrink-0">
              <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
            </div>
          </button>
        );
      })}
      
      <div className="border-t border-border">
        <button
          onClick={handleViewAllAssessments}
          className="flex items-center space-x-3 px-4 py-3 text-sm text-popover-foreground hover:bg-muted transition-colors duration-200 w-full text-left"
        >
          <Icon name="ExternalLink" size={16} />
          <span>View All Assessments</span>
        </button>
      </div>
    </div>
  );
};

export default AssessmentDropdown;


